import { useCallback, useEffect, useRef, useState } from "react"
import { fetchInfluencerProfile } from "../influencerProfileApi"

export const PROFILE_STATUS = {
  LOADING: "loading",
  READY: "ready",     // a profile or official-account answer to render
  NOT_FOUND: "not_found", // a real, valid answer (§1/§6) - never an error state
  ERROR: "error",
}

/**
 * Loads one creator's profile (INFLUENCER_PROFILE_API.md). `profileUrl` is
 * tried first when the caller has it (the common case: navigated here from
 * a leaderboard/favourites card, which passes its row's real `profile_url`
 * via router state); `platform`+`username` is the fallback used for a
 * direct visit or hard refresh of `/influencer-explorer/:platform/:username`
 * with no router state to carry the real URL - §2 explicitly supports a bare
 * handle with no host as long as `platform` is passed alongside it.
 *
 * `appIdFilter`/`videosPerApp` let one app's `AppCoverageCard` request its
 * own full post list (§8: "pass app_id with videos_per_app=50") without
 * this hook needing to know that's what's happening - it's just more
 * request params.
 */
export default function useInfluencerProfile({ profileUrl, platform, username, appIdFilter, videosPerApp } = {}) {
  const [data, setData] = useState(null)
  const [status, setStatus] = useState(PROFILE_STATUS.LOADING)
  const [error, setError] = useState(null)
  const requestTokenRef = useRef(0)

  const resolvedUrl = profileUrl || (username ? `@${username}` : null)

  const load = useCallback(async () => {
    if (!resolvedUrl) {
      setStatus(PROFILE_STATUS.ERROR)
      setError("No profile to load.")
      return
    }

    const requestToken = ++requestTokenRef.current
    setError(null)
    setStatus(prev => (prev === PROFILE_STATUS.READY ? PROFILE_STATUS.READY : PROFILE_STATUS.LOADING))

    try {
      const result = await fetchInfluencerProfile({
        profileUrl: resolvedUrl,
        platform,
        appId: appIdFilter,
        videosPerApp,
      })
      if (requestToken !== requestTokenRef.current) return
      setData(result)
      setStatus(PROFILE_STATUS.READY)
    } catch (err) {
      if (requestToken !== requestTokenRef.current) return
      if (err.notFound) {
        setData(null)
        setStatus(PROFILE_STATUS.NOT_FOUND)
      } else {
        setStatus(PROFILE_STATUS.ERROR)
        setError(err.message || "Something went wrong.")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedUrl, platform, appIdFilter, videosPerApp])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedUrl, platform, appIdFilter, videosPerApp])

  return { data, status, error, reload: load }
}
